import {
  MdAssignment,
  MdDescription,
  MdHomeWork,
  MdPlayCircle,
} from "react-icons/md";
import { studentPalette } from "../constants/studentPalette";
import { courseChapterPath } from "./studentCoursePaths";

export const RECENT_CONTENT_TYPE_META = {
  video: { label: "Video", Icon: MdPlayCircle, accent: studentPalette.secondary },
  exam: { label: "Exam", Icon: MdAssignment, accent: studentPalette.primary },
  pdf: { label: "PDF", Icon: MdDescription, accent: studentPalette.primary },
  homework: { label: "Homework", Icon: MdHomeWork, accent: studentPalette.secondary },
};

function formatDuration(seconds) {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return null;
  const mins = Math.floor(total / 60);
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return `${mins} min`;
}

function parseDate(value) {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : 0;
}

function buildSubtitle(section, detail) {
  const courseName = section?.name;
  if (courseName && detail) return `${courseName} · ${detail}`;
  return courseName || detail || "";
}

/** Parse API / ISO start_time to ms, or 0 if invalid. */
export function parseStartTimeMs(startTime) {
  if (startTime == null || startTime === "") return 0;
  const raw = String(startTime).trim();
  // Laravel "Y-m-d H:i:s" is safer as ISO-ish for Date parsing
  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const t = new Date(normalized).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Human label for time until start_time when it is still in the future.
 * @returns {string|null} e.g. "Starts in 2d 5h" or null if started / invalid
 */
export function formatTimeRemainingUntil(startTime, now = Date.now()) {
  const start = parseStartTimeMs(startTime);
  if (!start || start <= now) return null;

  let seconds = Math.floor((start - now) / 1000);
  const days = Math.floor(seconds / 86400);
  seconds %= 86400;
  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;
  const minutes = Math.floor(seconds / 60);

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${Math.max(minutes, 1)}m`);

  return `Starts in ${parts.slice(0, 2).join(" ")}`;
}

/** Build sorted recent-content rows from student performance analytics. */
export function buildRecentItems(performanceData) {
  const items = [];

  (performanceData?.recent_videos || []).forEach((v) => {
    const title = v?.title || v?.over_view || "Video session";
    items.push({
      id: `video-${v.id}`,
      type: "video",
      title,
      subtitle: buildSubtitle(v.section, formatDuration(v.duration) || "Video session"),
      date: v.created_at,
      to: courseChapterPath(v.section, v.chapter_id),
      startTime: null,
    });
  });

  (performanceData?.recent_exams || []).forEach((ex) => {
    const title = ex?.name || "Exam";
    const startTime = ex?.start_time || null;
    items.push({
      id: `exam-${ex.id}`,
      type: "exam",
      title,
      subtitle: buildSubtitle(
        ex.section,
        ex?.timer == 1 && ex?.time ? `${ex.time} min timed` : "Practice exam"
      ),
      date: ex.created_at,
      to: courseChapterPath(ex.section, ex.section?.laravel_through_key),
      startTime,
    });
  });

  (performanceData?.recent_pdfs || []).forEach((pdf) => {
    items.push({
      id: `pdf-${pdf.id}`,
      type: "pdf",
      title: pdf?.title || pdf?.over_view || "PDF lesson",
      subtitle: buildSubtitle(pdf.section, "PDF document"),
      date: pdf.created_at,
      to: courseChapterPath(pdf.section, pdf.chapter_id),
      startTime: null,
    });
  });

  (performanceData?.recent_homeworks || []).forEach((hw) => {
    items.push({
      id: `hw-${hw.id}`,
      type: "homework",
      title: hw?.name || hw?.homework || "Homework",
      subtitle: buildSubtitle(hw.section, "Homework assignment"),
      date: hw.created_at,
      to: courseChapterPath(hw.section, hw.chapter_id),
      startTime: null,
    });
  });

  return items.sort((a, b) => parseDate(b.date) - parseDate(a.date));
}
